import { CarPdfViewer } from "@app/components/PdfViewer";
import { CarPdfDocument } from "@app/components/PdfDocument";
import { CarPdfPage, defaultClientPageSize } from "@app/components/PdfPage";
import { useUser } from "@app/pages/settings/useUser";
import { usePortfolioGroupInfo } from "@app/pages/assetAllocation/usePortfolioGroupInfo";
import {
  dataIdsToShowConfig,
  usePortfolioGroupVersionData,
} from "@app/pages/assetAllocation/usePortfolioGroupVersionData";
import { usePortfolioGroupVersionStatTableData } from "@app/pages/assetAllocation/usePortfolioGroupVersionStatTableData";
import { HistoricalMarketSummaryPdfChart } from "./HistoricalMarketSummaryPdfChart";
import { HistoricalMarketSummaryPdfTable } from "./HistoricalMarketSummaryPdfTable";

const CHART_HEIGHT = 196;

interface ModuleScenarioAnalysisReportProps {
  portfolioGroupId: string;
  moduleId: string;
  dataIds: string[];
  footer: string;
}

export const ModuleScenarioAnalysisReport = ({
  portfolioGroupId,
  moduleId,
  dataIds,
  footer,
}: ModuleScenarioAnalysisReportProps) => {
  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const { portfolioGroupVersionId, publishedVersionId, isSinglePortfolioMode } =
    groupInfo;

  const showConfig = dataIdsToShowConfig(dataIds);

  const data = usePortfolioGroupVersionData({
    versionId: portfolioGroupVersionId,
    publishedVersionId,
    show: showConfig,
    hideCash: true,
    filterByModuleId: moduleId,
  });

  const versionStatTableData = usePortfolioGroupVersionStatTableData({
    bands: data.tableData.bands,
    skip: data.assetAllocationIsLoading,
    simulationSuffix: groupInfo.getSimulationSuffix(true),
  });

  const user = useUser();

  const moduleName = data.getModuleNameById(moduleId);

  const title = `Historical Markets Summary - ${isSinglePortfolioMode ? groupInfo.title : moduleName}`;

  return (
    <CarPdfViewer
      isLoading={
        user.isLoading ||
        groupInfo.isLoading ||
        data.isLoading ||
        versionStatTableData.isLoading
      }
    >
      <CarPdfDocument title={title} author={user.userFullName}>
        {
          <CarPdfPage
            sectionTitle="Analyze"
            reportTitle={title}
            footer={footer}
          >
            <HistoricalMarketSummaryPdfChart
              style={{ marginTop: 28 }}
              width={defaultClientPageSize.width}
              height={CHART_HEIGHT}
              data={versionStatTableData.statTableData}
              moduleId={moduleId}
              isWhiteContext
            />

            <HistoricalMarketSummaryPdfTable
              style={{ marginTop: 54 }}
              tableData={data.tableData}
              moduleId={moduleId}
              rows={versionStatTableData.statTableData.historicalStatRows}
            />
          </CarPdfPage>
        }
      </CarPdfDocument>
    </CarPdfViewer>
  );
};
